import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Button, Grid, Tabs, Tab,
  IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem,
  TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
  fetchDataType,
  deleteDataType,
  fetchDataIndicators,
  addDataTypeIndicator,
  replaceDataTypeIndicator,
  removeDataTypeIndicator,
  addDataTypeCriteria,
  deleteDataTypeCriteria,
  addDataTypeCriteriaValue,
  deleteDataTypeCriteriaValue,
  fetchDataItemsByDataType,
} from "../../api/companyApi";
import { wrapTextCell } from "../../utils/gridUtils";
import { useNotification } from "../../providers/NotificationProvider";
import DetailSection from "../../components/common/DetailSection";
import DetailField from "../../components/common/DetailField";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import DraggablePaper from "../../components/common/DraggablePaper";

const DATA_CLASS_LABELS = {
  MasterData: "dataType.class.MasterData",
  TransactionData: "dataType.class.TransactionData",
  AccountData: "dataType.class.AccountData",
  ReferenceData: "dataType.class.ReferenceData",
  ManagementData: "dataType.class.ManagementData",
  Document: "dataType.class.Document",
  Image: "dataType.class.Image",
  Video: "dataType.class.Video",
  Audio: "dataType.class.Audio",
  None: "dataType.class.None",
};

const EMPTY_KPI_FORM = { indicatorName: "", unit: "", measuringMethod: "", evaluationStandard: "", description: "" };

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

export default function DataTypeDetailPage() {
  const { businessDomainId, dataTypeId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [data, setData] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [availableIndicators, setAvailableIndicators] = useState([]);

  // ── DataItem list ──
  const [dataItems, setDataItems] = useState([]);

  // DataType KPI state
  const [kpiDialogOpen, setKpiDialogOpen] = useState(false);
  const [kpiEditMode, setKpiEditMode] = useState(false);
  const [kpiEditTarget, setKpiEditTarget] = useState(null);
  const [selectedIndicatorObj, setSelectedIndicatorObj] = useState("");
  const [kpiForm, setKpiForm] = useState(EMPTY_KPI_FORM);
  const [kpiConfirmOpen, setKpiConfirmOpen] = useState(false);
  const [kpiDeleteTarget, setKpiDeleteTarget] = useState(null);

  // Criteria state
  const [criteriaDialogOpen, setCriteriaDialogOpen] = useState(false);
  const [criteriaForm, setCriteriaForm] = useState({ classificationCriteriaCd: "", classificationCriteriaName: "" });
  const [criteriaConfirmOpen, setCriteriaConfirmOpen] = useState(false);
  const [criteriaDeleteTarget, setCriteriaDeleteTarget] = useState(null);

  // Criteria Value state
  const [selectedCriteria, setSelectedCriteria] = useState(null);
  const [cvDialogOpen, setCvDialogOpen] = useState(false);
  const [cvForm, setCvForm] = useState({ classificationCriteriaValueCd: "", classificationCriteriaValueName: "" });
  const [cvConfirmOpen, setCvConfirmOpen] = useState(false);
  const [cvDeleteTarget, setCvDeleteTarget] = useState(null);

  const loadData = useCallback(async () => {
    try { setData(await fetchDataType(dataTypeId)); }
    catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataTypeId]);

  const loadDataItems = useCallback(async () => {
    try {
      const items = await fetchDataItemsByDataType(dataTypeId);
      setDataItems(Array.isArray(items) ? items : []);
    } catch (e) { notify.apiError(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataTypeId]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadDataItems(); }, [loadDataItems]);

  useEffect(() => {
    (async () => {
      try { setAvailableIndicators(await fetchDataIndicators()); } catch (e) { /* ignore */ }
    })();
  }, []);

  // ── Delete data type ──
  const handleDelete = async () => {
    try {
      await deleteDataType(dataTypeId);
      notify.success(t("message.delete.success"));
      setConfirmOpen(false);
      navigate(`/design/${businessDomainId}/data-types`, { replace: true });
    } catch (e) { notify.apiError(e); }
  };

  // ── DataType KPI CRUD ──
  const openKpiAddDialog = () => {
    setKpiEditMode(false); setKpiEditTarget(null);
    setSelectedIndicatorObj(""); setKpiForm(EMPTY_KPI_FORM); setKpiDialogOpen(true);
  };
  const openKpiEditDialog = (indicator) => {
    setKpiEditMode(true); setKpiEditTarget(indicator);
    setSelectedIndicatorObj("");
    setKpiForm({ indicatorName: indicator.indicatorName || "", unit: indicator.unit || "", measuringMethod: indicator.measuringMethod || "", evaluationStandard: indicator.evaluationStandard || "", description: indicator.description || "" });
    setKpiDialogOpen(true);
  };
  const handleIndicatorSelect = (e) => {
    const selected = e.target.value;
    setSelectedIndicatorObj(selected);
    if (selected === "OTHER") {
      setKpiForm(EMPTY_KPI_FORM); // 「その他」は全フィールドを自由入力
    } else if (selected && selected !== "NONE") {
      setKpiForm({ indicatorName: selected.indicatorName || "", unit: selected.unit || "", measuringMethod: selected.measuringMethod || "", evaluationStandard: selected.evaluationStandard || "", description: selected.description || "" });
    } else {
      setKpiForm(EMPTY_KPI_FORM);
    }
  };
  const handleKpiSave = async () => {
    if (!kpiEditMode && selectedIndicatorObj === "OTHER" && !kpiForm.indicatorName.trim()) {
      notify.error(t("dataKpi.error.nameRequired")); return;
    }
    if (!kpiForm.indicatorName) { notify.error(t("message.error.required")); return; }
    try {
      if (kpiEditMode) {
        await replaceDataTypeIndicator(dataTypeId, { previousIndicator: { indicatorName: kpiEditTarget.indicatorName, unit: kpiEditTarget.unit, measuringMethod: kpiEditTarget.measuringMethod, evaluationStandard: kpiEditTarget.evaluationStandard, description: kpiEditTarget.description }, postIndicator: { ...kpiForm } });
      } else {
        if ((data.indicators || []).some((ind) => ind.indicatorName === kpiForm.indicatorName)) { notify.error(t("dataKpi.error.duplicate")); return; }
        await addDataTypeIndicator(dataTypeId, { ...kpiForm, createdAt: new Date().toISOString() });
      }
      notify.success(kpiEditMode ? t("message.update.success") : t("message.create.success"));
      setKpiDialogOpen(false); loadData();
    } catch (e) { notify.apiError(e); }
  };
  const handleKpiDelete = async () => {
    try {
      await removeDataTypeIndicator(dataTypeId, { indicatorName: kpiDeleteTarget.indicatorName, unit: kpiDeleteTarget.unit, measuringMethod: kpiDeleteTarget.measuringMethod, evaluationStandard: kpiDeleteTarget.evaluationStandard, description: kpiDeleteTarget.description });
      notify.success(t("message.delete.success")); loadData();
    } catch (e) { notify.apiError(e); }
    setKpiConfirmOpen(false); setKpiDeleteTarget(null);
  };

  // ── Criteria CRUD ──
  const handleCriteriaSave = async () => {
    if (!criteriaForm.classificationCriteriaCd || !criteriaForm.classificationCriteriaName) { notify.error(t("message.error.required")); return; }
    try {
      await addDataTypeCriteria(dataTypeId, { ...criteriaForm, createdAt: new Date().toISOString() });
      notify.success(t("message.create.success")); setCriteriaDialogOpen(false); loadData();
    } catch (e) { notify.apiError(e); }
  };
  const handleCriteriaDelete = async () => {
    try { await deleteDataTypeCriteria(dataTypeId, criteriaDeleteTarget); notify.success(t("message.delete.success")); loadData(); }
    catch (e) { notify.apiError(e); }
    setCriteriaConfirmOpen(false); setCriteriaDeleteTarget(null);
  };

  // ── Criteria Value CRUD ──
  const handleCvSave = async () => {
    if (!cvForm.classificationCriteriaValueCd || !cvForm.classificationCriteriaValueName) { notify.error(t("message.error.required")); return; }
    try {
      await addDataTypeCriteriaValue(dataTypeId, { classificationCriteria: selectedCriteria, classificationCriteriaValueCd: cvForm.classificationCriteriaValueCd, classificationCriteriaValueName: cvForm.classificationCriteriaValueName, createdValueAt: new Date().toISOString() });
      notify.success(t("message.create.success")); setCvDialogOpen(false); loadData();
    } catch (e) { notify.apiError(e); }
  };
  const handleCvDelete = async () => {
    try { await deleteDataTypeCriteriaValue(dataTypeId, cvDeleteTarget); notify.success(t("message.delete.success")); loadData(); }
    catch (e) { notify.apiError(e); }
    setCvConfirmOpen(false); setCvDeleteTarget(null);
  };

  if (!data) return null;

  const dataClassLabel = DATA_CLASS_LABELS[data.dataClass] ? t(DATA_CLASS_LABELS[data.dataClass]) : data.dataClass || "";
  const indicators = data.indicators || [];
  const criterias = data.classificationCriterias || [];
  const criteriaValues = selectedCriteria
    ? (data.classificationCriteriaValues || []).filter((v) => v.classificationCriteria?.classificationCriteriaName === selectedCriteria.classificationCriteriaName)
    : [];

  const diBasePath = `/design/${businessDomainId}/data-types/${dataTypeId}/data-items`;

  // ── Column definitions ──
  const diColumns = [
    { field: "dataItemName", headerName: t("dataItem.name"), flex: 1, minWidth: 180 },
    { field: "dataDomainName", headerName: t("dataItem.dataDomain"), width: 150 },
    { field: "primaryKey", headerName: t("dataItem.primaryKey"), width: 80, type: "boolean" },
    { field: "foreignKey", headerName: t("dataItem.foreignKey"), width: 80, type: "boolean" },
  ];
  const kpiColumns = [
    { field: "indicatorName", headerName: t("dataKpi.indicatorName"), flex: 1, minWidth: 150 },
    { field: "unit", headerName: t("dataKpi.unit"), width: 100 },
    { field: "measuringMethod", headerName: t("dataKpi.measuringMethod"), flex: 1, minWidth: 150, renderCell: wrapTextCell },
    { field: "evaluationStandard", headerName: t("dataKpi.evaluationStandard"), flex: 1, minWidth: 150, renderCell: wrapTextCell },
    {
      field: "actions", headerName: t("common.actions"), width: 100, sortable: false,
      renderCell: (p) => (
        <Box>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); openKpiEditDialog(p.row); }}><EditIcon fontSize="small" /></IconButton>
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setKpiDeleteTarget(p.row); setKpiConfirmOpen(true); }}><DeleteIcon fontSize="small" /></IconButton>
        </Box>
      ),
    },
  ];
  const criteriaColumns = [
    { field: "classificationCriteriaCd", headerName: t("dataCriteria.code"), width: 160 },
    { field: "classificationCriteriaName", headerName: t("dataCriteria.name"), flex: 1, minWidth: 200 },
    {
      field: "actions", headerName: t("common.actions"), width: 80, sortable: false,
      renderCell: (p) => (
        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setCriteriaDeleteTarget({ classificationCriteriaCd: p.row.classificationCriteriaCd, classificationCriteriaName: p.row.classificationCriteriaName }); setCriteriaConfirmOpen(true); }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];
  const cvColumns = [
    { field: "classificationCriteriaValueCd", headerName: t("criteriaValue.code"), width: 160 },
    { field: "classificationCriteriaValueName", headerName: t("criteriaValue.name"), flex: 1, minWidth: 200 },
    {
      field: "actions", headerName: t("common.actions"), width: 80, sortable: false,
      renderCell: (p) => (
        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); setCvDeleteTarget(p.row); setCvConfirmOpen(true); }}><DeleteIcon fontSize="small" /></IconButton>
      ),
    },
  ];

  return (
    <Box>
      {/* ── Header ── */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">{t("dataType.detail.title")}</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/design/${businessDomainId}/data-types/${dataTypeId}/edit`)}>{t("common.edit")}</Button>
          <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmOpen(true)}>{t("common.delete")}</Button>
        </Box>
      </Box>

      {/* ── Basic fields ── */}
      <DetailSection>
        <Grid container spacing={2}>
          <Grid size={12}><DetailField label={t("dataType.name")} value={data.dataTypeName} /></Grid>
          <Grid size={12}><DetailField label={t("dataType.dataClass")} value={dataClassLabel} /></Grid>
          <Grid size={12}><DetailField label={t("dataType.description")} value={data.dataTypeDescription} /></Grid>
          <Grid size={{ xs: 12, md: 6 }}><DetailField label={t("dataType.dataOwner")} value={data.dataOwner} /></Grid>
          <Grid size={{ xs: 12, md: 6 }}><DetailField label={t("dataType.dataOwnerMailAddress")} value={data.address} /></Grid>
          <Grid size={{ xs: 12, md: 6 }}><DetailField label={t("dataType.dataSensitivityLevel")} value={data.dataSensitivityLevel} /></Grid>
          <Grid size={{ xs: 12, md: 6 }}><DetailField label={t("dataType.dataRestrictedCategory")} value={data.dataRestrictedCategory} /></Grid>
          <Grid size={12}><DetailField label={t("common.url")} value={data.url} /></Grid>
        </Grid>
      </DetailSection>

      {/* ── Tabs ── */}
      <Box sx={{ mt: 3, borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tabIndex} onChange={(_, v) => { setTabIndex(v); setSelectedCriteria(null); }}>
          <Tab label={t("dataItem.title")} />
          <Tab label={t("dataKpi.title")} />
          <Tab label={t("dataCriteria.title")} />
        </Tabs>
      </Box>

      {/* ── Tab 0: データ項目 ── */}
      <TabPanel value={tabIndex} index={0}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1">{t("dataItem.list.title")}</Typography>
          <Button variant="contained" size="small" startIcon={<AddIcon />}
            onClick={() => navigate(`${diBasePath}/create`)}>
            {t("common.add")}
          </Button>
        </Box>
        <DataGrid rows={dataItems} columns={diColumns} getRowId={(r) => r.dataItemId}
          autoHeight pageSizeOptions={[10, 25]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          onRowClick={(params) => navigate(`${diBasePath}/${params.row.dataItemId}`, { state: params.row })}
          sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }} />
      </TabPanel>

      {/* ── Tab 1: 指標 ── */}
      <TabPanel value={tabIndex} index={1}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle1">{t("dataKpi.list.title")}</Typography>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openKpiAddDialog}>{t("common.add")}</Button>
        </Box>
        <DataGrid rows={indicators} columns={kpiColumns} getRowId={(r) => r.indicatorName}
          key={indicators.map((i) => i.indicatorName).join(",")}
          autoHeight pageSizeOptions={[5, 10]}
          initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
          disableRowSelectionOnClick getRowHeight={() => 'auto'} />
      </TabPanel>

      {/* ── Tab 2: 分類基準 ── */}
      <TabPanel value={tabIndex} index={2}>
        {selectedCriteria ? (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <IconButton onClick={() => setSelectedCriteria(null)} size="small"><ArrowBackIcon /></IconButton>
              <Typography variant="subtitle1">{selectedCriteria.classificationCriteriaName}：{t("criteriaValue.list.title")}</Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="contained" size="small" startIcon={<AddIcon />}
                onClick={() => { setCvForm({ classificationCriteriaValueCd: "", classificationCriteriaValueName: "" }); setCvDialogOpen(true); }}>
                {t("common.add")}
              </Button>
            </Box>
            <DataGrid rows={criteriaValues} columns={cvColumns}
              getRowId={(r) => r.classificationCriteriaValueCd}
              key={criteriaValues.map((v) => v.classificationCriteriaValueCd).join(",")}
              autoHeight pageSizeOptions={[5, 10]}
              initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
              disableRowSelectionOnClick />
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography variant="subtitle1">{t("dataCriteria.list.title")}</Typography>
              <Button variant="contained" size="small" startIcon={<AddIcon />}
                onClick={() => { setCriteriaForm({ classificationCriteriaCd: "", classificationCriteriaName: "" }); setCriteriaDialogOpen(true); }}>
                {t("common.add")}
              </Button>
            </Box>
            <DataGrid rows={criterias} columns={criteriaColumns}
              getRowId={(r) => r.classificationCriteriaCd}
              key={criterias.map((c) => c.classificationCriteriaCd).join(",")}
              autoHeight pageSizeOptions={[5, 10]}
              initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
              disableRowSelectionOnClick
              onRowClick={(params) => setSelectedCriteria(params.row)}
              sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
            />
          </Box>
        )}
      </TabPanel>

      <Box sx={{ mt: 3 }}><Button variant="outlined" onClick={() => navigate(`/design/${businessDomainId}/data-types`)}>{t("common.back")}</Button></Box>

      {/* ── Confirm dialogs ── */}
      <ConfirmDialog open={confirmOpen} onConfirm={handleDelete} onCancel={() => setConfirmOpen(false)} />
      <ConfirmDialog open={kpiConfirmOpen} onConfirm={handleKpiDelete} onCancel={() => { setKpiConfirmOpen(false); setKpiDeleteTarget(null); }} />
      <ConfirmDialog open={criteriaConfirmOpen} onConfirm={handleCriteriaDelete} onCancel={() => { setCriteriaConfirmOpen(false); setCriteriaDeleteTarget(null); }} />
      <ConfirmDialog open={cvConfirmOpen} onConfirm={handleCvDelete} onCancel={() => { setCvConfirmOpen(false); setCvDeleteTarget(null); }} />

      {/* ── DataType KPI Dialog ── */}
      <Dialog open={kpiDialogOpen} onClose={() => setKpiDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{kpiEditMode ? t("dataKpi.edit.title") : t("dataKpi.create.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {!kpiEditMode && (
              <FormControl fullWidth required>
                <InputLabel>{t("dataKpi.indicatorName")}</InputLabel>
                <Select value={selectedIndicatorObj} label={t("dataKpi.indicatorName")} onChange={handleIndicatorSelect}>
                  {availableIndicators.map((ind) => (
                    <MenuItem key={ind.indicatorName} value={ind}>{ind.indicatorName}</MenuItem>
                  ))}
                  <MenuItem value="OTHER">{t("dataKpi.other")}</MenuItem>
                </Select>
              </FormControl>
            )}
            {!kpiEditMode && selectedIndicatorObj === "OTHER" && (
              <TextField fullWidth required label={t("dataKpi.indicatorName")} value={kpiForm.indicatorName}
                onChange={(e) => setKpiForm({ ...kpiForm, indicatorName: e.target.value })} />
            )}
            {(kpiEditMode || selectedIndicatorObj !== "") && (
              <>
                {kpiEditMode && (
                  <TextField fullWidth required label={t("dataKpi.indicatorName")} value={kpiForm.indicatorName}
                    onChange={(e) => setKpiForm({ ...kpiForm, indicatorName: e.target.value })} />
                )}
                <TextField fullWidth label={t("dataKpi.unit")} value={kpiForm.unit}
                  onChange={(e) => setKpiForm({ ...kpiForm, unit: e.target.value })} />
                <TextField fullWidth multiline rows={3} label={t("dataKpi.measuringMethod")} value={kpiForm.measuringMethod}
                  onChange={(e) => setKpiForm({ ...kpiForm, measuringMethod: e.target.value })} />
                <TextField fullWidth multiline rows={3} label={t("dataKpi.evaluationStandard")} value={kpiForm.evaluationStandard}
                  onChange={(e) => setKpiForm({ ...kpiForm, evaluationStandard: e.target.value })} />
                <TextField fullWidth multiline rows={3} label={t("dataKpi.description")} value={kpiForm.description}
                  onChange={(e) => setKpiForm({ ...kpiForm, description: e.target.value })} />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKpiDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleKpiSave}
            disabled={!kpiEditMode && (selectedIndicatorObj === "" || (selectedIndicatorObj === "OTHER" && !kpiForm.indicatorName.trim()))}>
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Criteria Add Dialog ── */}
      <Dialog open={criteriaDialogOpen} onClose={() => setCriteriaDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("dataCriteria.create.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField fullWidth required multiline label={t("dataCriteria.code")} value={criteriaForm.classificationCriteriaCd}
              onChange={(e) => setCriteriaForm({ ...criteriaForm, classificationCriteriaCd: e.target.value })} />
            <TextField fullWidth required multiline label={t("dataCriteria.name")} value={criteriaForm.classificationCriteriaName}
              onChange={(e) => setCriteriaForm({ ...criteriaForm, classificationCriteriaName: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCriteriaDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleCriteriaSave}>{t("common.add")}</Button>
        </DialogActions>
      </Dialog>

      {/* ── Criteria Value Add Dialog ── */}
      <Dialog open={cvDialogOpen} onClose={() => setCvDialogOpen(false)} maxWidth="sm" fullWidth PaperComponent={DraggablePaper}>
        <DialogTitle data-drag-handle sx={{ cursor: "move", userSelect: "none" }}>{t("criteriaValue.create.title")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField fullWidth required multiline label={t("criteriaValue.code")} value={cvForm.classificationCriteriaValueCd}
              onChange={(e) => setCvForm({ ...cvForm, classificationCriteriaValueCd: e.target.value })} />
            <TextField fullWidth required multiline label={t("criteriaValue.name")} value={cvForm.classificationCriteriaValueName}
              onChange={(e) => setCvForm({ ...cvForm, classificationCriteriaValueName: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCvDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="contained" onClick={handleCvSave}>{t("common.add")}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
